import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

interface DebugInfo {
  envVars: {
    API_BASE_URL: string;
    NODE_ENV: string;
  };
  apiConnection: {
    isAvailable: boolean;
    testResult?: { success: boolean; message: string };
  };
}

export const DebugPanel: React.FC = () => {
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadDebugInfo = async () => {
    setIsLoading(true);
    try {
      // Environment Variables for API
      const envVars = {
        API_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071',
        NODE_ENV: process.env.NODE_ENV || 'development'
      };

      // API Connection testen
      const apiConnection = {
        isAvailable: true, // API service is always available
        testResult: undefined as { success: boolean; message: string } | undefined
      };

      // Verbindungstest durchführen
      try {
        const state = await apiService.loadAppState();
        apiConnection.testResult = {
          success: true,
          message: `API erfolgreich verbunden. ${state?.tables?.length || 0} Tische gefunden.`
        };
      } catch (error) {
        apiConnection.testResult = {
          success: false,
          message: `API Test fehlgeschlagen: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
        };
      }

      setDebugInfo({ 
        envVars,
        apiConnection 
      });
    } catch (error) {
      console.error('Debug Info Fehler:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      loadDebugInfo();
    }
  }, [isVisible]);

  // Debug Panel Sichtbarkeitslogik
  const urlParams = new URLSearchParams(window.location.search);
  const debugParam = urlParams.get('debug');
  const showDebugParam = urlParams.get('showdebug');
  
  const shouldShow = process.env.NODE_ENV === 'development' 
    ? (debugParam !== 'false')  // In Development: standardmäßig an, außer explizit debug=false
    : (debugParam === 'true' || showDebugParam === '1');  // In Production: nur wenn explizit aktiviert

  if (!shouldShow) {
    return null;
  }

  return (
    <div style={{ 
      position: 'fixed', 
      top: '10px', 
      left: '10px',  // Moved to left side to avoid settings button
      zIndex: 9999,
      background: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      fontFamily: 'monospace',
      fontSize: '12px',
      borderRadius: '8px',
      padding: '10px',
      maxWidth: '400px'
    }}>
      <button 
        onClick={() => setIsVisible(!isVisible)}
        style={{
          background: '#007ACC',
          color: 'white',
          border: 'none',
          padding: '5px 10px',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: isVisible ? '10px' : '0'
        }}
      >
        {isVisible ? '🐛 Debug schließen' : '🐛 Debug öffnen'}
      </button>

      {isVisible && (
        <div>
          <button 
            onClick={loadDebugInfo}
            disabled={isLoading}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '10px',
              marginLeft: '10px'
            }}
          >
            {isLoading ? '🔄 Laden...' : '🔄 Aktualisieren'}
          </button>

          {debugInfo && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', color: '#ffc107' }}>🔧 Environment Variables</h4>
              <div style={{ marginBottom: '15px' }}>
                <div><strong>API_BASE_URL:</strong> {debugInfo.envVars.API_BASE_URL}</div>
                <div><strong>NODE_ENV:</strong> {debugInfo.envVars.NODE_ENV}</div>
              </div>

              <h4 style={{ margin: '0 0 10px 0', color: '#17a2b8' }}>🚀 API Status</h4>
              <div>
                <div><strong>Service verfügbar:</strong> {debugInfo.apiConnection.isAvailable ? '✅ Ja' : '❌ Nein'}</div>
                {debugInfo.apiConnection.testResult && (
                  <div style={{ marginTop: '5px' }}>
                    <strong>Verbindungstest:</strong> {debugInfo.apiConnection.testResult.success ? '✅' : '❌'}
                    <br />
                    <span style={{ color: debugInfo.apiConnection.testResult.success ? '#28a745' : '#dc3545' }}>
                      {debugInfo.apiConnection.testResult.message}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '4px' }}>
                <small>
                  💡 <strong>Hinweise:</strong><br />
                  • API läuft lokal auf http://localhost:7071<br />
                  • In Produktion automatisch über Azure Static Web Apps API<br />
                  • Sichere Authentifikation über Managed Identity
                </small>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DebugPanel;